import { useFormContext } from "react-hook-form";
import MetaAgeDemographicInsightsCard from "./MetaAgeDemographicInsightsCard";
import MetaGenderDemographicInsightsCard from "./MetaGenderDemographicInsightsCard";
import MetaPublisherPlatformInsightsCard from "./MetaPublisherPlatformInsightsCard";
import {
  filterAgeDemographicInsightsRows,
  filterGenderInsightsRowsWithoutUnknown,
  sortPublisherPlatformInsightsRows,
} from "./MetaDemographicInsightsTables";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { META } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_PERCENTAGE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD,
  IMPRESSIONS,
  RESULTS,
  CTR,
  CLICKS,
  AGE_RANGE_PLACEHOLDER,
  GENDERS_PLACEHOLDER,
  FEMALES,
  MALES,
  LOCATIONS_PLACEHOLDER,
  INTERESTS_PLACEHOLDER,
  DEMOGRAPHICS_PLACEHOLDER,
  BEHAVIORS_PLACEHOLDER,
  CUSTOM_AUDIENCES,
  ROAS,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { openLink } from "../../../../../utils/string";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import PerformingAssetsBento, {
  PerformingAssetsBentoItem,
} from "../components/PerformingAssetsBento";
import PerformingAssetsSegmentation from "../components/PerformingAssetsSegmentation";
import PerformingAssetsCreative from "../components/PerformingAssetsCreative";
import { buildPerformingAssetSummary } from "../components/buildPerformingAssetSummary";
import {
  canShowPerformingEntity,
  findCampaignEntityByID,
} from "../components/performingAssetsMetrics";

const MetaPerformingAssets = () => {
  const { t } = useTranslationApp();
  const { campaign, campaignID, getCostByMainCurrency } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);
  const demographicInsights = statistics?.[META]?.campaign?.demographicInsights;
  const placementInsights = statistics?.[META]?.campaign?.placementInsights;

  // This function selects the best ad group based on mainAction if available, otherwise by clicks
  const getBestAdGroup = (adGroupsStatistics) => {
    if (!adGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(adGroupsStatistics).forEach(([adGroupId, stats]) => {
      if (!stats) return;
      // Check if this ad group has mainAction
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            adGroupId,
            statistics: stats,
          };
        }
      }
      // Always check for best by clicks as fallback
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          adGroupId,
          statistics: stats,
        };
      }
    });

    // If at least one ad group has mainAction, return the best by mainAction, else by clicks
    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // This function selects the best ad based on mainAction if available, otherwise by clicks
  const getBestAd = (adsGroups, statistics) => {
    if (!Array.isArray(adsGroups) || !statistics) return null;

    let bestAdByMainAction = null;
    let bestAdByClicks = null;
    let hasMainAction = false;

    adsGroups.forEach((adGroup) => {
      if (adGroup?.platform !== META || !Array.isArray(adGroup.ads)) return;

      adGroup.ads.forEach((ad) => {
        if (!ad?.id) return;
        const adStats = statistics[ad.id];
        if (!adStats) return;

        // Check if this ad has mainAction
        if (adStats.mainAction && adStats.mainAction > 0) {
          hasMainAction = true;
          if (
            !bestAdByMainAction ||
            adStats.mainAction > bestAdByMainAction.statistics.mainAction
          ) {
            bestAdByMainAction = {
              adGroupId: adGroup.id,
              adId: ad.id,
              statistics: adStats,
            };
          }
        }

        // Always check for best by clicks as fallback
        if (
          !bestAdByClicks ||
          (adStats.click || 0) > (bestAdByClicks.statistics.click || 0)
        ) {
          bestAdByClicks = {
            adGroupId: adGroup.id,
            adId: ad.id,
            statistics: adStats,
          };
        }
      });
    });
    // If at least one ad has mainAction, return the best by mainAction, else by clicks
    return hasMainAction ? bestAdByMainAction : bestAdByClicks;
  };

  const metaStats = statistics?.[META] ?? {};
  const bestAdGroup = getBestAdGroup(metaStats.adsGroups);
  const bestAd = getBestAd(campaign?.adsGroups, metaStats.ads);

  const getAdGroupByID = (id) => {
    const adGroup = findCampaignEntityByID({
      entities: campaign?.adsGroups,
      id,
      platform: META,
    });
    if (!adGroup?.id) return {};
    return { adGroup, adGroupID: adGroup.id };
  };

  const getAdByID = (adId) => {
    if (!adId) {
      return {};
    }
    const adGroupsMeta = (campaign?.adsGroups ?? []).filter(
      (adGroup) => adGroup.platform === META
    );

    const foundAdGroup = adGroupsMeta.find(
      (adGroup) =>
        Array.isArray(adGroup?.ads) && adGroup.ads.some((ad) => ad.id === adId)
    );

    if (!foundAdGroup) {
      return {};
    }

    const ad = foundAdGroup.ads.find((item) => item.id === adId);
    if (!ad?.id) return {};
    const adGroupIndex = adGroupsMeta.findIndex(
      (group) => group.id === foundAdGroup.id
    );
    const adGroupID = foundAdGroup.id;

    return {
      ad,
      adID: ad.id,
      adGroupIndex,
      adGroupID,
    };
  };

  const { adGroup = {}, adGroupID } = getAdGroupByID(bestAdGroup?.adGroupId);
  const { ad = {}, adID, adGroupID: adGroupIDAd } = getAdByID(bestAd?.adId);

  const demographicAgeRows = filterAgeDemographicInsightsRows(
    demographicInsights?.age
  );
  const demographicGenderRows = filterGenderInsightsRowsWithoutUnknown(
    demographicInsights?.gender
  );
  const publisherPlatformRows = sortPublisherPlatformInsightsRows(
    placementInsights?.publisherPlatform
  );

  const summaryLabels = {
    t,
    impressionsLabel: t(IMPRESSIONS),
    ctrLabel: t(CTR),
    clicksLabel: t(CLICKS),
    resultsLabel: t(RESULTS),
    valueNumber: VALUE_NUMBER,
    valuePercentage: VALUE_WITH_PERCENTAGE,
  };

  const showBestAdGroup = canShowPerformingEntity({
    entity: adGroup,
    statistics: bestAdGroup?.statistics,
  });
  const showBestAd = canShowPerformingEntity({
    entity: ad,
    statistics: bestAd?.statistics,
  });

  const bestAdGroupStats = showBestAdGroup
    ? buildPerformingAssetSummary({
        ...summaryLabels,
        statistics: bestAdGroup.statistics,
        roasLabel: bestAdGroup.statistics.roas ? t(ROAS) : null,
      })
    : [];

  const bestAdStats = showBestAd
    ? buildPerformingAssetSummary({
        ...summaryLabels,
        statistics: bestAd.statistics,
      })
    : [];

  const creativeUrl = ad?.mediaUrls?.[0];
  const sections = [
    showBestAdGroup ? (
      <PerformingAssetsBentoItem key="bestAdGroup" span={8}>
        <PerformingAssetsCard
          platform={META}
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP)}
          subtitle={adGroup.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${META}&adGroupID=${adGroupID}`
            )
          }
          statistics={bestAdGroupStats}
          nextContent={<BestAdGroupSegmentation adGroup={adGroup} />}
        />
      </PerformingAssetsBentoItem>
    ) : null,
    showBestAd ? (
      <PerformingAssetsBentoItem key="bestAd" span={4}>
        <PerformingAssetsCard
          platform={META}
          layout="creative"
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD)}
          subtitle={ad.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${META}&adGroupID=${adGroupIDAd}&adID=${adID}`
            )
          }
          statistics={bestAdStats}
          media={
            creativeUrl ? <PerformingAssetsCreative url={creativeUrl} /> : null
          }
        />
      </PerformingAssetsBentoItem>
    ) : null,
    demographicAgeRows.length ? (
      <PerformingAssetsBentoItem key="age" span={12}>
        <MetaAgeDemographicInsightsCard
          ageRows={demographicAgeRows}
          getCostByMainCurrency={getCostByMainCurrency}
        />
      </PerformingAssetsBentoItem>
    ) : null,
    demographicGenderRows.length ? (
      <PerformingAssetsBentoItem key="gender" span={6}>
        <MetaGenderDemographicInsightsCard
          genderRows={demographicGenderRows}
          getCostByMainCurrency={getCostByMainCurrency}
        />
      </PerformingAssetsBentoItem>
    ) : null,
    publisherPlatformRows.length ? (
      <PerformingAssetsBentoItem key="platforms" span={6}>
        <MetaPublisherPlatformInsightsCard
          publisherPlatformRows={publisherPlatformRows}
          getCostByMainCurrency={getCostByMainCurrency}
        />
      </PerformingAssetsBentoItem>
    ) : null,
  ].filter(Boolean);

  if (!sections.length) return null;

  return <PerformingAssetsBento>{sections}</PerformingAssetsBento>;
};

const SEGMENTATION_FIELDS = [
  "ageRange",
  "genders",
  "locations",
  "interests",
  "demographics",
  "behaviors",
  "customAudiences",
];
const BestAdGroupSegmentation = ({ adGroup = {} }) => {
  const { t } = useTranslationApp();

  const getSegmentationFields = () => {
    const segmentationFieldsData = [];

    SEGMENTATION_FIELDS.forEach((field) => {
      const fieldData = adGroup[field];
      if (!fieldData || fieldData?.length === 0) return;
      if (field === "ageRange") {
        const [minAge, maxAge] = fieldData || [];
        segmentationFieldsData.push({
          label: t(AGE_RANGE_PLACEHOLDER),
          value: `${minAge}-${maxAge}`,
        });
      } else if (field === "genders") {
        const genders = fieldData || {};
        segmentationFieldsData.push({
          label: t(GENDERS_PLACEHOLDER),
          value: Object.keys(genders)
            .filter((gender) => genders[gender] === true)
            .map((gender) => t(gender === "male" ? MALES : FEMALES))
            .join(", "),
        });
      } else {
        const value = fieldData.map((item) => item.name).join(", ");
        let label = "";
        switch (field) {
          case "locations":
            label = t(LOCATIONS_PLACEHOLDER);
            break;
          case "interests":
            label = t(INTERESTS_PLACEHOLDER);
            break;
          case "demographics":
            label = t(DEMOGRAPHICS_PLACEHOLDER);
            break;
          case "behaviors":
            label = t(BEHAVIORS_PLACEHOLDER);
            break;
          case "customAudiences":
            label = t(CUSTOM_AUDIENCES);
            break;
        }

        segmentationFieldsData.push({
          label,
          value,
        });
      }
    });

    return segmentationFieldsData;
  };

  const segmentationFields = getSegmentationFields();

  return <PerformingAssetsSegmentation fields={segmentationFields} />;
};

export default MetaPerformingAssets;
